import { useFormik } from 'formik'
import { useSnackbar } from 'notistack'
import * as Yup from 'yup'

import { ConsoleDialog } from '@/components/ConsoleDialog'
import { FormikTextField } from '@/components/inputs/FormikTextField'
import { setMediaFeedDelay } from '@/service/mediaFeedDelayService'
import { FormDialogProps } from '@/types/forms'
import { formikSaveButtonProps } from '@/utils/forms'
import { notifyError } from '@/utils/notifyError'

export type MediaFeedDelayFormikValues = {
  delay: number
}

export type MediaFeedDelayFormProps = FormDialogProps<{ delay: number }, { operatorId: string }>

const mediaFeedDelayValidationSchema = Yup.object({
  delay: Yup.number()
    .integer('Decimals are not allowed')
    .required('Media feed delay is required')
    .min(1, 'Delay must be at least 1 second')
    .max(3600, 'Delay cannot exceed 3600 seconds (1 hour)')
})

export const MediaFeedDelayForm = ({
  onClose,
  onSubmit,
  open = false,
  data,
  defaultValues
}: MediaFeedDelayFormProps) => {
  const { enqueueSnackbar } = useSnackbar()

  const formik = useFormik<MediaFeedDelayFormikValues>({
    initialValues: {
      delay: defaultValues?.delay ?? 5
    },
    enableReinitialize: true,

    validationSchema: mediaFeedDelayValidationSchema,

    onSubmit: async (values, { setSubmitting }) => {
      if (!data?.operatorId || !values.delay) return

      try {
        setSubmitting(true)
        await setMediaFeedDelay(data.operatorId, values.delay)
        onSubmit?.()
        enqueueSnackbar('Successfully updated media feed delay', {
          variant: 'success'
        })
        setSubmitting(false)
        handleClose()
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Unknown error')
        notifyError(`Failed to update media feed delay: ${error.message}`)
        setSubmitting(false)
        throw err
      }
    }
  })

  const handleClose = () => {
    onClose?.()
    formik.resetForm()
  }

  return (
    <ConsoleDialog
      open={open}
      title="Media Feed Delay"
      description="Set the media feed delay in seconds. The default value for all operators is 5 seconds."
      onClose={handleClose}
      dialogSize="xs"
      SaveButtonProps={formikSaveButtonProps(formik)}
    >
      <FormikTextField
        name="delay"
        label="Media Feed Delay"
        type="number"
        formik={formik}
        autoFocus
        required
        InputProps={{
          endAdornment: 'seconds'
        }}
      />
    </ConsoleDialog>
  )
}
